import { FC, useEffect } from 'react'

import styled from 'styled-components'
import { useForm, Controller } from 'react-hook-form'

import { Button } from 'src/Components/Button'
import { Input } from 'src/Components/Input/index.tsx'
import { Select } from 'src/Components/Select'
import { Textarea } from 'src/Components/Textarea/index.tsx'
import { Checkbox } from 'src/Components/Checkbox/index.tsx'
import { HintTooltip } from 'src/Components/HintTooltip'
import { useIsAdmin } from 'src/Hooks/useIsAdmin.ts'
import {
  Equipment,
  UpdateEquipmentBody,
  DriveTypeEnum,
} from 'src/Redux/api/equipmentApi.ts'
import { FileInput } from 'src/Components/FileInput'
import { PhotoGallery } from 'src/Components/PhotoGallery'
import { Modal } from 'src/Components/Modal'
import { device } from 'src/Helpers/constants.ts'
import { ModelType } from 'src/Redux/api/modelTypesApi.ts'
import {
  useGetAllModelsQuery,
  useAddModelImagesMutation,
  useDeleteModelImageMutation,
  useSetModelDefaultToolMutation,
} from 'src/Redux/api/modelApi.ts'

const ModalContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 45px;
  gap: 20px;
`

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  max-width: 1000px;
  gap: 20px;

  @media ${device.tabletMax} {
    flex-direction: column;
  }
`

const RowContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 300px;
`

const RightRow = styled(RowContainer)``

interface AddTransportModalProps {
  onClose: () => void
  onUpdateEquipment: (equipment: UpdateEquipmentBody) => void
  isOpened: boolean
  equipmentToEdit?: Equipment | null
  modelTypes?: ModelType[]
}

export const EditTransportModal: FC<AddTransportModalProps> = ({
  isOpened,
  onClose,
  onUpdateEquipment,
  equipmentToEdit,
  modelTypes,
}) => {
  const isAdmin = useIsAdmin()
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UpdateEquipmentBody>({
    defaultValues: {
      year: new Date().getFullYear(),
      registrationNumber: '',
      engineNumber: '',
      color: '',
      vin: '',
      passportNumber: '',
      estValue: 0,
      isOwned: true,
      name: '',
      type: '',
      driveType: DriveTypeEnum.WHEEL,
      baseRentalCost: 0,
      fuelEfficiencySummer: 0,
      fuelEfficiencyWinter: 0,
      engineCapacity: 0,
      description: '',
      operationalLogs: '',
    },
  })

  // Update form values when equipmentToEdit changes
  useEffect(() => {
    if (equipmentToEdit) {
      reset({
        year: equipmentToEdit.year || new Date().getFullYear(),
        registrationNumber: equipmentToEdit.registrationNumber || '',
        engineNumber: equipmentToEdit.engineNumber || '',
        color: equipmentToEdit.color || '',
        vin: equipmentToEdit.vin || '',
        passportNumber: equipmentToEdit.passportNumber || '',
        estValue: equipmentToEdit.estValue || 0,
        isOwned: equipmentToEdit.isOwned ?? true,
        name: equipmentToEdit.model?.name || '',
        type: String(equipmentToEdit.model?.modelTypeId),
        driveType:
          (equipmentToEdit.model?.driveType as DriveTypeEnum) ||
          DriveTypeEnum.WHEEL,
        baseRentalCost: equipmentToEdit.model?.baseRentalCost || 0,
        fuelEfficiencySummer: equipmentToEdit.model?.fuelEfficiencySummer || 0,
        fuelEfficiencyWinter: equipmentToEdit.model?.fuelEfficiencyWinter || 0,
        engineCapacity: equipmentToEdit.model?.engineCapacity || 0,
        description: equipmentToEdit.model?.description || '',
        operationalLogs: equipmentToEdit.model?.operationalLogs || '',
      })
    }
  }, [equipmentToEdit, reset])

  const modelId = equipmentToEdit?.model?.id
  const { data: modelsData } = useGetAllModelsQuery({ all: true })
  const currentModel = modelsData?.data?.find((m) => m.id === modelId)
  const modelImages = currentModel?.images || []
  const [addModelImages] = useAddModelImagesMutation()
  const [deleteModelImage] = useDeleteModelImageMutation()
  const [setModelDefaultTool] = useSetModelDefaultToolMutation()
  const modelTools = currentModel?.modelTools || []
  const defaultToolId = modelTools.find((tool) => tool.isDefault)?.id
  const defaultToolSelectData = modelTools.map((tool) => ({
    value: String(tool.id),
    label: tool.name,
  }))

  const onDefaultToolChange = (value: string | null): void => {
    if (!modelId) return
    setModelDefaultTool({ modelId, toolId: value ? Number(value) : null })
  }

  const onAddModelPhotos = (files: File[]): void => {
    if (!modelId) return
    addModelImages({ modelId, images: files })
  }

  const onDeleteModelPhoto = (imageId: number): void => {
    if (!modelId) return
    deleteModelImage({ modelId, imageId })
  }

  const onSubmit = (data: UpdateEquipmentBody): void => {
    onUpdateEquipment(data)
    reset()
    onClose()
  }

  const typeSelectData = modelTypes?.map((type) => {
    return {
      value: String(type.id),
      label: type.label,
    }
  })

  const driveTypeSelectData = [
    { value: DriveTypeEnum.CRAWLER, label: 'Гусеничный' },
    { value: DriveTypeEnum.WHEEL, label: 'Колесный' },
  ]

  return (
    <Modal opened={isOpened} onClose={onClose} title='Редактировать технику'>
      <ModalContainer>
        <Container>
          <RowContainer>
            <Controller
              name='name'
              control={control}
              render={({ field }) => (
                <Input
                  placeholder='Наименование'
                  label='Наименование'
                  error={errors.name?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='type'
              control={control}
              render={({ field }) => (
                <Select
                  placeholder='Тип техники'
                  label='Тип техники'
                  data={typeSelectData}
                  error={errors.type?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='driveType'
              control={control}
              render={({ field }) => (
                <Select
                  placeholder='Вид движителя'
                  label='Вид движителя'
                  data={driveTypeSelectData}
                  error={errors.driveType?.message}
                  {...field}
                />
              )}
            />

            <HintTooltip
              label='Изменять стоимость аренды может только администратор'
              disabled={isAdmin}
            >
              <Controller
                name='baseRentalCost'
                control={control}
                render={({ field }) => (
                  <Input
                    placeholder='Базовая стоимость аренды'
                    type='number'
                    disabled={!isAdmin}
                    label='Базовая стоимость аренды'
                    error={errors.baseRentalCost?.message}
                    {...field}
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(parseFloat(e.target.value) || 0)
                    }
                  />
                )}
              />
            </HintTooltip>

            <Controller
              name='fuelEfficiencySummer'
              control={control}
              render={({ field }) => (
                <Input
                  placeholder='Расход топлива (лето)'
                  type='number'
                  label='Расход топлива (лето)'
                  error={errors.fuelEfficiencySummer?.message}
                  {...field}
                  value={field.value?.toString() || ''}
                  onChange={(e) =>
                    field.onChange(parseFloat(e.target.value) || 0)
                  }
                />
              )}
            />

            <Controller
              name='fuelEfficiencyWinter'
              control={control}
              render={({ field }) => (
                <Input
                  placeholder='Расход топлива (зима)'
                  type='number'
                  label='Расход топлива (зима)'
                  error={errors.fuelEfficiencyWinter?.message}
                  {...field}
                  value={field.value?.toString() || ''}
                  onChange={(e) =>
                    field.onChange(parseFloat(e.target.value) || 0)
                  }
                />
              )}
            />

            <Controller
              name='engineCapacity'
              control={control}
              render={({ field }) => (
                <Input
                  placeholder='Объем двигателя'
                  type='number'
                  label='Объем двигателя'
                  error={errors.engineCapacity?.message}
                  {...field}
                  value={field.value?.toString() || ''}
                  onChange={(e) =>
                    field.onChange(parseFloat(e.target.value) || 0)
                  }
                />
              )}
            />

            <Controller
              name='operationalLogs'
              control={control}
              render={({ field }) => (
                <Select
                  placeholder='Вид первичных учетных документов'
                  label='Вид первичных учетных документов'
                  data={[
                    { value: 'Путевой лист', label: 'Путевой лист' },
                    { value: 'ЭСМ3', label: 'ЭСМ3' },
                  ]}
                  error={errors.operationalLogs?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='description'
              control={control}
              render={({ field }) => (
                <Textarea
                  placeholder='Описание'
                  label='Описание'
                  minRows={3}
                  autosize
                  {...field}
                />
              )}
            />

            <Controller
              name='image'
              control={control}
              render={({ field: { onChange, ...field } }) => (
                <FileInput
                  label='Фото техники'
                  placeholder='Выберите фото'
                  accept='image/*'
                  onChange={onChange}
                  {...field}
                />
              )}
            />

            {modelId && modelTools.length > 0 && (
              <HintTooltip label='Подставляется в КП автоматически и не пишется в наименовании — например, ковш экскаватора. Другое навесное можно выбрать в КП'>
                <Select
                  label='Навесное по умолчанию'
                  placeholder='Без навесного по умолчанию'
                  data={defaultToolSelectData}
                  value={defaultToolId ? String(defaultToolId) : null}
                  onChange={onDefaultToolChange}
                  clearable
                  data-testid='default-tool-select'
                />
              </HintTooltip>
            )}

            {modelId && (
              <PhotoGallery
                title='Дополнительные фото модели'
                addLabel='Добавить фото модели'
                images={modelImages}
                onAdd={onAddModelPhotos}
                onDelete={onDeleteModelPhoto}
              />
            )}
          </RowContainer>
          <RightRow>
            <Controller
              name='year'
              control={control}
              rules={{
                required: 'Год обязателен',
                min: {
                  value: 1900,
                  message: 'Год должен быть не менее 1900',
                },
                max: {
                  value: new Date().getFullYear() + 1,
                  message: `Год должен быть не более ${new Date().getFullYear() + 1}`,
                },
              }}
              render={({ field }) => (
                <Input
                  placeholder='Год выпуска'
                  label='Год выпуска'
                  type='number'
                  error={errors.year?.message}
                  {...field}
                  value={field.value?.toString() || ''}
                  onChange={(e) =>
                    field.onChange(
                      parseInt(e.target.value) || new Date().getFullYear(),
                    )
                  }
                />
              )}
            />

            <Controller
              name='registrationNumber'
              control={control}
              render={({ field }) => (
                <Input
                  label='Регистрационный номер'
                  placeholder='Регистрационный номер'
                  error={errors.registrationNumber?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='engineNumber'
              control={control}
              render={({ field }) => (
                <Input
                  label='Номер двигателя'
                  placeholder='Номер двигателя'
                  error={errors.engineNumber?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='color'
              control={control}
              render={({ field }) => (
                <Input
                  placeholder='Цвет'
                  label='Цвет'
                  error={errors.color?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='vin'
              control={control}
              render={({ field }) => (
                <Input
                  placeholder='VIN номер'
                  label='VIN номер'
                  error={errors.vin?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='passportNumber'
              control={control}
              render={({ field }) => (
                <Input
                  placeholder='Номер паспорта ТС'
                  label='Номер паспорта ТС'
                  error={errors.passportNumber?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='estValue'
              control={control}
              render={({ field }) => (
                <Input
                  placeholder='Оценочная стоимость, руб.'
                  label='Оценочная стоимость'
                  type='number'
                  error={errors.estValue?.message}
                  {...field}
                  value={field.value?.toString() || ''}
                  onChange={(e) =>
                    field.onChange(parseFloat(e.target.value) || 0)
                  }
                />
              )}
            />

            <Controller
              name='isOwned'
              control={control}
              render={({ field: { value, onChange } }) => (
                <HintTooltip label='Снимите, если техника заведена только для выставления КП и не входит в ваш парк'>
                  <Checkbox
                    label='В собственности'
                    checked={value ?? true}
                    onChange={(event) => onChange(event.currentTarget.checked)}
                    data-testid='equipment-is-owned'
                  />
                </HintTooltip>
              )}
            />
          </RightRow>
        </Container>
        <Button onClick={handleSubmit(onSubmit)}>Сохранить изменения</Button>
      </ModalContainer>
    </Modal>
  )
}
